import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Phone, MessageCircle, CheckCircle, Star, Clock, Shield, ChevronDown, Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';

const Section = styled.section`
  padding: 5rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 3rem;
  font-size: clamp(2rem, 4vw, 3rem);
  color: ${brandTheme.colors.secondary[900]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin: 3rem 0;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.primary[700]} 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: white;
  }

  h3 {
    color: ${brandTheme.colors.primary[600]};
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  p {
    color: ${brandTheme.colors.neutral.gray[600]};
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  .icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.primary[700]} 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    color: white;
  }

  h4 {
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
  }

  p {
    color: ${brandTheme.colors.neutral.gray[600]};
    font-size: 0.9rem;
  }
`;

const PostcodesSection = styled.section`
  background: ${brandTheme.colors.neutral.gray[50]};
  padding: 5rem 0;
`;

const PostcodesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin: 3rem 0;
`;

const PostcodeCard = styled(motion.div)`
  background: white;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${brandTheme.colors.primary[600]};
    transform: translateY(-2px);
  }

  .postcode {
    font-size: 1.1rem;
    font-weight: bold;
    color: ${brandTheme.colors.primary[600]};
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.primary[700]} 100%);
  color: white;
  padding: 5rem 0;
  text-align: center;
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const TestimonialsSection = styled.section`
  padding: 5rem 0;
  background: white;
`;

const TestimonialCard = styled(motion.div)`
  background: ${brandTheme.colors.neutral.gray[50]};
  border-radius: 15px;
  padding: 2rem;
  margin: 1rem 0;
  border-left: 4px solid ${brandTheme.colors.primary[600]};

  .stars {
    display: flex;
    gap: 0.25rem;
    margin-bottom: 1rem;
    color: #fbbf24;
  }

  .text {
    font-style: italic;
    margin-bottom: 1rem;
    color: ${brandTheme.colors.neutral.gray[700]};
  }

  .author {
    font-weight: 600;
    color: ${brandTheme.colors.secondary[900]};
  }
`;

const FAQSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, ${brandTheme.colors.neutral.gray[50]} 0%, ${brandTheme.colors.primary[50]} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="%23f3f4f6" opacity="0.3"/><circle cx="75" cy="75" r="1" fill="%23f3f4f6" opacity="0.3"/><circle cx="50" cy="10" r="0.5" fill="%23f3f4f6" opacity="0.2"/><circle cx="10" cy="60" r="0.5" fill="%23f3f4f6" opacity="0.2"/><circle cx="90" cy="40" r="0.5" fill="%23f3f4f6" opacity="0.2"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.1;
  }
`;

const FAQContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
`;

const FAQHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  .subtitle {
    font-size: 1.1rem;
    color: ${brandTheme.colors.primary[600]};
    font-weight: 600;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .title {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 1rem;
    font-weight: 700;
    line-height: 1.2;
  }
  
  .description {
    font-size: 1.2rem;
    color: ${brandTheme.colors.neutral.gray[600]};
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    margin-bottom: 3rem;
    
    .subtitle {
      font-size: 1rem;
    }
    
    .title {
      font-size: clamp(2rem, 6vw, 2.5rem);
    }
    
    .description {
      font-size: 1.1rem;
      padding: 0 1rem;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 2.5rem;
    
    .subtitle {
      font-size: 0.9rem;
    }
    
    .title {
      font-size: clamp(1.75rem, 7vw, 2rem);
    }
    
    .description {
      font-size: 1rem;
      padding: 0 0.5rem;
    }
  }
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 20px;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.primary[600]} 50%, ${brandTheme.colors.primary[500]} 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 2rem 2.5rem;
  background: white;
  border: none;
  text-align: left;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${brandTheme.colors.secondary[900]};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;

  &:hover {
    background: linear-gradient(135deg, ${brandTheme.colors.primary[50]} 0%, ${brandTheme.colors.neutral.gray[50]} 100%);
    color: ${brandTheme.colors.primary[700]};
  }

  .icon {
    transition: all 0.3s ease;
    color: ${brandTheme.colors.primary[600]};
    background: ${brandTheme.colors.primary[100]};
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &.active {
    background: linear-gradient(135deg, ${brandTheme.colors.primary[50]} 0%, ${brandTheme.colors.neutral.gray[50]} 100%);
    color: ${brandTheme.colors.primary[700]};
    
    .icon {
      background: ${brandTheme.colors.primary[600]};
      color: white;
      transform: rotate(180deg);
    }
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem;
    font-size: 1.1rem;

    .icon {
      width: 36px;
      height: 36px;
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem 1.25rem;
    font-size: 1rem;
    line-height: 1.4;

    .icon {
      width: 32px;
      height: 32px;
    }
  }
`;

const FAQAnswer = styled(motion.div)`
  padding: 1.5rem 2.5rem 2rem 2.5rem;
  color: ${brandTheme.colors.neutral.gray[700]};
  line-height: 1.7;
  border-top: 1px solid ${brandTheme.colors.neutral.gray[200]};
  background: linear-gradient(135deg, ${brandTheme.colors.neutral.gray[50]} 0%, white 100%);
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
    font-size: 0.95rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    padding: 1rem 1.25rem 1.25rem 1.25rem;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const GallerySection = styled.section`
  padding: 5rem 0;
  background: white;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  img {
    width: 100%;
    height: 250px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ImageModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  backdrop-filter: blur(10px);
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
  text-align: center;
`;

const ModalDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  text-align: center;
  max-width: 600px;
  line-height: 1.5;
  margin: 0 0 2rem 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    width: 40px;
    height: 40px;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-50%) scale(1.1);
  }

  &.prev {
    left: 2rem;
  }

  &.next {
    right: 2rem;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    
    &.prev {
      left: 1rem;
    }

    &.next {
      right: 1rem;
    }
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    bottom: 1rem;
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;


const ProcessSection = styled.section`
  padding: 5rem 0;
  background: ${brandTheme.colors.neutral.gray[50]};
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
  margin: 3rem 0;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  position: relative;

  .step-number {
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 30px;
    height: 30px;
    background: ${brandTheme.colors.primary[600]};
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.9rem;
  }

  .icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[600]} 0%, ${brandTheme.colors.primary[700]} 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1rem auto 1.5rem;
    color: white;
  }

  h4 {
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 1rem;
    font-size: 1.25rem;
  }

  p {
    color: ${brandTheme.colors.neutral.gray[600]};
    line-height: 1.6;
  }
`;

const GreenwichPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openImageModal = (index: number) => {
    setSelectedImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImageIndex(null);
  };

  const goToPreviousImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(selectedImageIndex === 0 ? galleryItems.length - 1 : selectedImageIndex - 1);
    }
  };

  const goToNextImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(selectedImageIndex === galleryItems.length - 1 ? 0 : selectedImageIndex + 1);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (selectedImageIndex !== null) {
      if (event.key === 'Escape') {
        closeImageModal();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPreviousImage();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNextImage();
      }
    }
  };

  // Add keyboard event listener when modal is open
  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImageIndex]);

  const services = [
    {
      title: "Resin Driveways",
      description: "Premium permeable driveways that enhance your property's curb appeal while meeting SUDS requirements.",
      icon: <MapPin size={24} />
    },
    {
      title: "Garden Pathways",
      description: "Beautiful resin bound pathways that guide visitors through your garden with style and durability.",
      icon: <MapPin size={24} />
    },
    {
      title: "Pool Decks",
      description: "Non-slip resin bound surfacing perfect for pool areas, combining safety with aesthetic appeal.",
      icon: <MapPin size={24} />
    },
    {
      title: "Commercial Surfacing",
      description: "Heavy-duty resin bound solutions for commercial properties, car parks, and public spaces.",
      icon: <MapPin size={24} />
    }
  ];

  const features = [
    {
      title: "10+ Year Warranty",
      description: "All our resin bound installations come with a comprehensive warranty for your peace of mind.",
      icon: <Shield size={24} />
    },
    {
      title: "Free Survey",
      description: "We provide complimentary site surveys across all Greenwich postcodes with no obligation.",
      icon: <CheckCircle size={24} />
    },
    {
      title: "Quick Installation",
      description: "Most projects completed within 1-2 days, minimizing disruption to your daily routine.",
      icon: <Clock size={24} />
    },
    {
      title: "5-Star Reviews",
      description: "Rated 5 stars by our customers across Greenwich and surrounding areas.",
      icon: <Star size={24} />
    }
  ];

  const testimonials = [
    {
      text: "Excellent service from start to finish. Our new resin driveway looks fantastic and the team was professional and clean.",
      author: "Sarah M., Greenwich",
      rating: 5
    },
    {
      text: "The resin bound pathway has transformed our garden. It's been 3 years and still looks as good as new.",
      author: "James L., Blackheath",
      rating: 5
    },
    {
      text: "Great value for money and excellent customer service. Highly recommend for anyone in Greenwich.",
      author: "Emma R., Deptford",
      rating: 5
    }
  ];

  const postcodes = [
    // SE10 (Greenwich)
    "SE10", "SE10 0", "SE10 1", "SE10 2", "SE10 3", "SE10 4", "SE10 5", "SE10 6", "SE10 7", "SE10 8", "SE10 9",
    // SE7 (Charlton)
    "SE7", "SE7 0", "SE7 1", "SE7 2", "SE7 3", "SE7 4", "SE7 5", "SE7 6", "SE7 7", "SE7 8", "SE7 9",
    // SE8 (Deptford)
    "SE8", "SE8 0", "SE8 1", "SE8 2", "SE8 3", "SE8 4", "SE8 5",
    // SE9 (Eltham)
    "SE9", "SE9 0", "SE9 1", "SE9 2", "SE9 3", "SE9 4", "SE9 5", "SE9 6", "SE9 7", "SE9 8", "SE9 9",
    // SE12 (Lee)
    "SE12", "SE12 0", "SE12 1", "SE12 2", "SE12 3", "SE12 4", "SE12 5", "SE12 6", "SE12 7", "SE12 8", "SE12 9",
    // SE13 (Lewisham)
    "SE13", "SE13 0", "SE13 1", "SE13 2", "SE13 3", "SE13 4", "SE13 5", "SE13 6", "SE13 7", "SE13 8", "SE13 9",
    // SE18 (Woolwich)
    "SE18", "SE18 0", "SE18 1", "SE18 2", "SE18 3", "SE18 4", "SE18 5", "SE18 6", "SE18 7", "SE18 8", "SE18 9",
    // SE28 (Thamesmead)
    "SE28", "SE28 0", "SE28 1", "SE28 2", "SE28 3", "SE28 4", "SE28 5",
    // SE2 (Abbey Wood)
    "SE2", "SE2 0", "SE2 1", "SE2 2", "SE2 3", "SE2 4", "SE2 5", "SE2 6", "SE2 7", "SE2 8", "SE2 9",
    // SE3 (Blackheath)
    "SE3", "SE3 0", "SE3 1", "SE3 2", "SE3 3", "SE3 4", "SE3 5", "SE3 6", "SE3 7", "SE3 8", "SE3 9"
  ];

  const galleryItems = [
    {
      image: "/images/resin-surfaces-bound-work1.jpeg",
      title: "Greenwich Park Driveway",
      description: "Premium granite resin bound driveway near Greenwich Park"
    },
    {
      image: "/images/resin-surfaces-bound-work2.jpeg",
      title: "Blackheath Pathway",
      description: "Elegant quartz pathway leading to Victorian townhouse"
    },
    {
      image: "/images/resin-surfaces-bound-work3.jpeg",
      title: "Deptford Commercial",
      description: "Durable resin bound surfacing for high-traffic commercial area"
    },
    {
      image: "/images/resin-surfaces-bound-work4.jpeg",
      title: "Charlton Courtyard",
      description: "Beautiful courtyard transformation with natural stone aggregates"
    },
    {
      image: "/images/resin-surfaces-bound-work5.jpeg",
      title: "Eltham Pool Deck",
      description: "Anti-slip resin bound pool deck with safety features"
    },
    {
      image: "/images/resin-surfaces-bound-work6.jpeg",
      title: "Lee Garden Path",
      description: "Permeable pathway enhancing garden aesthetics"
    },
    {
      image: "/images/resin-surfaces-bound-work7.jpeg",
      title: "Woolwich Heritage Area",
      description: "Heritage-compliant surfacing near historic landmarks"
    },
    {
      image: "/images/resin-surfaces-bound-work8.jpeg",
      title: "Thamesmead Road",
      description: "Luxury residential driveway with premium finish"
    }
  ];


  const processSteps = [
    {
      step: 1,
      title: "Free Survey",
      description: "We visit your Greenwich property to assess the area and discuss your requirements.",
      icon: <MapPin size={24} />
    },
    {
      step: 2,
      title: "Design & Quote",
      description: "We create a detailed design and provide a comprehensive quote with no hidden costs.",
      icon: <Camera size={24} />
    },
    {
      step: 3,
      title: "Preparation",
      description: "We prepare the existing surface and ensure proper drainage for optimal results.",
      icon: <Shield size={24} />
    },
    {
      step: 4,
      title: "Installation",
      description: "Our expert team installs your resin bound surface with precision and care.",
      icon: <CheckCircle size={24} />
    },
    {
      step: 5,
      title: "Completion",
      description: "We clean up thoroughly and provide maintenance guidance for long-lasting results.",
      icon: <Star size={24} />
    }
  ];

  const faqs = [
            {
              question: "How much does a resin driveway cost in Greenwich?",
              answer: "Resin driveway costs in Greenwich typically range from £80-£120 per square metre, depending on the size, complexity, and chosen aggregates. For a standard single-car driveway (approximately 20m²), you can expect to pay between £1,600-£2,400. Our team provides free, no-obligation quotes for all Greenwich postcodes."
            },
            {
              question: "Do I need planning permission for a resin driveway in Greenwich?",
              answer: "Most residential resin driveways in Greenwich don't require planning permission, especially if they're permeable and meet SUDS (Sustainable Urban Drainage Systems) requirements. However, if your property is in a conservation area or has specific restrictions, we'll advise you on the necessary permissions during our free survey."
            },
            {
              question: "How long does resin driveway installation take in Greenwich?",
              answer: "Most resin driveway installations in Greenwich are completed within 1-2 days. The exact timeframe depends on the size and complexity of your project. We work efficiently to minimize disruption to your daily routine and keep you informed throughout the process."
            },
            {
              question: "Is resin bound surfacing suitable for Greenwich's weather?",
              answer: "Yes, resin bound surfacing is excellent for Greenwich's climate. It's UV stable, frost resistant, and won't crack in cold weather. The permeable nature also helps with drainage during heavy rainfall, making it ideal for London's weather conditions."
            },
            {
              question: "Can I choose different colours and finishes for my Greenwich driveway?",
              answer: "Absolutely! We offer a wide range of natural stone aggregates in various colours, sizes, and finishes. From classic granite to contemporary quartz, we can create a unique look that complements your Greenwich property's architecture and your personal style."
            },
            {
              question: "What maintenance does a resin driveway require?",
              answer: "Resin driveways require minimal maintenance. Regular sweeping and occasional gentle pressure washing are usually sufficient. We provide detailed care instructions and offer maintenance services to keep your driveway looking pristine for years to come."
            },
            {
              question: "Do you work in all Greenwich postcodes?",
              answer: "Yes, we provide resin bound surfacing services across all Greenwich postcodes including SE10, SE7, SE8, SE9, SE12, SE13, SE18, SE28, SE2, SE3, and surrounding areas. Our team covers Greenwich Park, Blackheath, Deptford, Charlton, Eltham, Lee, Woolwich, and all other Greenwich locations."
            },
            {
              question: "What warranty do you offer on resin driveways?",
              answer: "We offer a comprehensive 10+ year warranty on all our resin bound installations. This covers materials and workmanship, giving you complete peace of mind. Our warranty is backed by our partnership with Vuba Surfaces and GCL Products, industry-leading manufacturers."
            },
            {
              question: "How does resin bound compare to other driveway materials?",
              answer: "Resin bound surfacing offers superior benefits compared to traditional materials. It's permeable (SUDS compliant), anti-slip, low maintenance, and provides excellent durability. Unlike tarmac or concrete, it won't crack or fade, and it's more environmentally friendly due to its permeable nature."
            },
            {
              question: "Can you install resin driveways in conservation areas?",
              answer: "Yes, we have extensive experience working in Greenwich's conservation areas including Greenwich Park and parts of Blackheath. We understand the specific requirements and can advise on planning permissions and suitable materials that meet conservation area guidelines while providing modern functionality."
            }
  ];

  return (
    <>
      <Helmet>
                <title>🏆 #1 Resin Driveways Greenwich | Premium Resin Bound Surfacing South East London | Greenwich Park, Blackheath, Deptford | Free Quotes</title>
                <meta name="description" content="🏆 #1 Resin Driveways Greenwich specialists! Premium resin bound surfacing in South East London's historic borough. Expert installation in Greenwich Park, Blackheath, Deptford, Charlton, Eltham, Lee, Woolwich. Partnered with Vuba Surfaces & GCL Products. 10+ year warranty, SUDS compliant, permeable, anti-slip surfaces. FREE quotes today!" />
                <meta name="keywords" content="resin driveways Greenwich, resin bound surfacing Greenwich, resin pathways Greenwich, resin pool decks Greenwich, resin driveways Greenwich Park, resin bound surfacing Blackheath, resin driveways Deptford, resin surfacing Charlton, resin driveways Eltham, resin bound surfacing Lee, resin driveway installers Greenwich, resin surfacing contractors Greenwich, resin bound specialists Greenwich, permeable driveways Greenwich, SUDS compliant surfacing Greenwich, anti-slip resin surfaces Greenwich, resin driveway cost Greenwich, resin driveway quotes Greenwich, best resin driveway company Greenwich, resin driveway near me Greenwich, resin surfacing South East London, resin bound installation South East London, commercial resin surfacing Greenwich, residential resin driveways Greenwich, resin driveway design Greenwich, resin bound maintenance Greenwich, resin surface repair Greenwich, resin bound vs tarmac Greenwich, resin driveway benefits Greenwich, resin surfacing experts Greenwich, premium resin driveways Greenwich, resin bound installation process Greenwich, resin driveways SE10, resin bound surfacing SE7, resin driveways SE8, resin surfacing SE9, resin bound SE12, resin driveways SE13, resin surfacing SE18, resin bound SE28, resin driveways SE2, resin surfacing SE3, resin driveway installers South East London, resin surfacing contractors South East London, resin bound specialists South East London, permeable driveways South East London, SUDS compliant surfacing South East London, anti-slip resin surfaces South East London, resin driveway cost South East London, resin driveway quotes South East London, best resin driveway company South East London, resin driveway near me South East London, resin surfacing London, resin bound installation London, commercial resin surfacing London, residential resin driveways London, resin driveway design London, resin bound maintenance London, resin surface repair London, resin bound vs tarmac London, resin driveway benefits London, resin surfacing experts London, premium resin driveways London, resin bound installation process London" />
                <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/resin-driveways-greenwich/" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta name="author" content="Resin Surfaces London" />
        <meta name="publisher" content="Resin Surfaces London" />
        <meta name="copyright" content="Resin Surfaces London" />
        <meta name="language" content="en-GB" />
                <meta name="geo.region" content="GB-LND" />
                <meta name="geo.placename" content="Greenwich, London" />
                <meta name="geo.position" content="51.4769;0.0000" />
                <meta name="ICBM" content="51.4769, 0.0000" />
        <meta name="theme-color" content="#1e40af" />
        <meta name="msapplication-TileColor" content="#1e40af" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/resin-driveways-greenwich/" />
                <meta property="og:title" content="🏆 #1 Resin Driveways Greenwich | Premium Resin Bound Surfacing South East London | Greenwich Park, Blackheath, Deptford | Free Quotes" />
                <meta property="og:description" content="🏆 #1 Resin Driveways Greenwich specialists! Premium resin bound surfacing in South East London's historic borough. Expert installation in Greenwich Park, Blackheath, Deptford, Charlton, Eltham, Lee, Woolwich. Partnered with Vuba Surfaces & GCL Products. 10+ year warranty, SUDS compliant, permeable, anti-slip surfaces. FREE quotes today!" />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content="Premium resin bound driveway installation in Greenwich, London" />
        <meta property="og:site_name" content="Resin Surfaces London" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content="https://www.resinsurfaceslondon.co.uk/resin-driveways-greenwich/" />
                <meta property="twitter:title" content="🏆 #1 Resin Driveways Greenwich | Premium Resin Bound Surfacing South East London | Greenwich Park, Blackheath, Deptford | Free Quotes" />
                <meta property="twitter:description" content="🏆 #1 Resin Driveways Greenwich specialists! Premium resin bound surfacing in South East London's historic borough. Expert installation in Greenwich Park, Blackheath, Deptford, Charlton, Eltham, Lee, Woolwich. Partnered with Vuba Surfaces & GCL Products. 10+ year warranty, SUDS compliant, permeable, anti-slip surfaces. FREE quotes today!" />
        <meta property="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
                <meta property="twitter:image:alt" content="Premium resin bound driveway installation in Greenwich, London" />
        <meta property="twitter:creator" content="@resinsurfaceslondon" />
        <meta property="twitter:site" content="@resinsurfaceslondon" />

        {/* Structured Data - Local Business */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
                    "name": "Resin Surfaces London - Greenwich",
                    "alternateName": "Resin Driveways Greenwich",
                    "description": "South East London's historic borough, home to Greenwich Park, Royal Observatory, and Cutty Sark. We provide premium resin bound surfacing for residential and commercial properties.",
                    "url": "https://www.resinsurfaceslondon.co.uk/resin-driveways-greenwich/",
            "telephone": "+447535935779",
            "email": "info@resinsurfaceslondon.co.uk",
                    "address": {
                      "@type": "PostalAddress",
                      "streetAddress": "Greenwich",
                      "addressLocality": "London",
                      "postalCode": "SE10",
                      "addressCountry": "GB"
                    },
                    "geo": {
                      "@type": "GeoCoordinates",
                      "latitude": "51.4769",
                      "longitude": "0.0000"
                    },
                    "areaServed": [
                      "Greenwich", "Greenwich Park", "Blackheath", "Deptford", "Charlton", "Eltham", "Lee", "Woolwich", "SE10", "SE7", "SE8", "SE9", "SE12", "SE13", "SE18", "SE28", "SE2", "SE3"
                    ],
            "serviceType": "Resin Bound Surfacing",
            "priceRange": "£££",
            "openingHours": "Mo-Fr 08:00-18:00, Sa 09:00-17:00",
            "paymentAccepted": "Cash, Credit Card, Bank Transfer",
            "currenciesAccepted": "GBP",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Resin Bound Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Resin Driveways",
                    "description": "Premium permeable driveways that enhance your property's curb appeal while meeting SUDS requirements."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Garden Pathways",
                    "description": "Beautiful resin bound pathways that guide visitors through your garden with style and durability."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Pool Decks",
                    "description": "Non-slip resin bound surfacing perfect for pool areas, combining safety with aesthetic appeal."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Commercial Surfacing",
                    "description": "Heavy-duty resin bound solutions for commercial properties, car parks, and public spaces."
                  }
                }
              ]
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": "Sarah M."
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "reviewBody": "Exceptional service and beautiful results. Our Mayfair driveway looks stunning and the team was professional throughout."
              },
              {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": "James R."
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "reviewBody": "Outstanding work in Marylebone. The resin bound surface is perfect and the installation was completed on time."
              }
            ]
          })}
        </script>

        {/* Structured Data - FAQ */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                        "name": "How much does a resin driveway cost in Greenwich?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Resin driveway costs in Greenwich typically range from £80-£120 per square metre, depending on the size, complexity, and chosen aggregates. For a standard single-car driveway (approximately 20m²), you can expect to pay between £1,600-£2,400. Our team provides free, no-obligation quotes for all Greenwich postcodes."
                        }
              },
              {
                "@type": "Question",
                        "name": "Do I need planning permission for a resin driveway in Greenwich?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Most residential resin driveways in Greenwich don't require planning permission, especially if they're permeable and meet SUDS (Sustainable Urban Drainage Systems) requirements. However, if your property is in a conservation area or has specific restrictions, we'll advise you on the necessary permissions during our free survey."
                        }
              },
              {
                "@type": "Question",
                        "name": "How long does resin driveway installation take in Greenwich?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Most resin driveway installations in Greenwich are completed within 1-2 days. The exact timeframe depends on the size and complexity of your project. We work efficiently to minimize disruption to your daily routine and keep you informed throughout the process."
                        }
              }
            ]
          })}
        </script>

        {/* Structured Data - Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.resinsurfaceslondon.co.uk/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Areas We Cover",
                "item": "https://www.resinsurfaceslondon.co.uk/areas/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                        "name": "Greenwich",
                        "item": "https://www.resinsurfaceslondon.co.uk/resin-driveways-greenwich/"
              }
            ]
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Resin Driveways Greenwich"
        subtitle="Premium resin bound surfacing specialists serving Greenwich and surrounding areas"
        badge="Local Experts • Professional Installation • 10+ Year Warranty"
        backgroundImage="/images/resin-surfaces-bound-work2.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <SectionTitle>Why Choose Resin Surfaces London for Greenwich?</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.2rem', color: brandTheme.colors.neutral.gray[600], marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
            South East London's historic borough, home to Greenwich Park, Royal Observatory, and Cutty Sark. We provide premium resin bound surfacing for residential and commercial properties.
          </p>
          
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{feature.icon}</div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </Section>

      <Section style={{ background: brandTheme.colors.primary[50] }}>
        <Container>
          <SectionTitle>Greenwich-Specific Benefits</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '3rem' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              style={{ background: 'white', padding: '2rem', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 style={{ color: brandTheme.colors.primary[600], marginBottom: '1rem' }}>Conservation Area Expertise</h3>
              <p style={{ color: brandTheme.colors.neutral.gray[600], lineHeight: 1.6 }}>
                We understand Greenwich's conservation area requirements and can advise on planning permissions and suitable materials that meet heritage guidelines while providing modern functionality.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              style={{ background: 'white', padding: '2rem', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 style={{ color: brandTheme.colors.primary[600], marginBottom: '1rem' }}>Luxury Property Experience</h3>
              <p style={{ color: brandTheme.colors.neutral.gray[600], lineHeight: 1.6 }}>
                Our team has extensive experience working on high-end properties in Mayfair, Marylebone, and Soho, ensuring the highest standards of workmanship and attention to detail.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              style={{ background: 'white', padding: '2rem', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' }}
            >
              <h3 style={{ color: brandTheme.colors.primary[600], marginBottom: '1rem' }}>SUDS Compliance</h3>
              <p style={{ color: brandTheme.colors.neutral.gray[600], lineHeight: 1.6 }}>
                All our resin bound surfaces are fully SUDS compliant, helping Greenwich meet its environmental obligations while providing beautiful, functional driveways and pathways.
              </p>
            </motion.div>
          </div>
        </Container>
      </Section>

      <Section style={{ background: brandTheme.colors.neutral.gray[50] }}>
        <Container>
          <SectionTitle>Our Services in Greenwich</SectionTitle>
          <ServicesGrid>
            {services.map((service, index) => (
              <Card
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <StyledButton to="/contact/" className="primary">
                  Get Quote <ArrowRight size={16} />
                </StyledButton>
              </Card>
            ))}
          </ServicesGrid>
        </Container>
      </Section>

      <GallerySection>
        <Container>
          <SectionTitle>Our Greenwich Work Gallery</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: brandTheme.colors.neutral.gray[600], marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
            Explore our portfolio of completed resin bound projects across Greenwich's most historic areas
          </p>
          <GalleryGrid>
            {galleryItems.map((item, index) => (
              <GalleryItem
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => openImageModal(index)}
              >
                <img src={item.image} alt={item.title} />
              </GalleryItem>
            ))}
          </GalleryGrid>
        </Container>
      </GallerySection>

      <ProcessSection>
        <Container>
          <SectionTitle>Our Greenwich Installation Process</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: brandTheme.colors.neutral.gray[600], marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
            From initial consultation to final completion, we ensure a seamless experience for every Greenwich project
          </p>
          <ProcessGrid>
            {processSteps.map((step, index) => (
              <ProcessCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{step.step}</div>
                <div className="icon">{step.icon}</div>
                <h4>{step.title}</h4>
                <p>{step.description}</p>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <TestimonialsSection>
        <Container>
          <SectionTitle>What Our Greenwich Customers Say</SectionTitle>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="text">"{testimonial.text}"</p>
              <p className="author">- {testimonial.author}</p>
            </TestimonialCard>
          ))}
        </Container>
      </TestimonialsSection>

      <PostcodesSection>
        <Container>
          <SectionTitle>Areas We Cover in Greenwich</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: brandTheme.colors.neutral.gray[600], marginBottom: '2rem' }}>
            We provide resin bound surfacing services across all Greenwich postcodes:
          </p>
          <PostcodesGrid>
            {postcodes.map((postcode, index) => (
              <PostcodeCard
                key={postcode}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.02 }}
                viewport={{ once: true }}
              >
                <div className="postcode">{postcode}</div>
              </PostcodeCard>
            ))}
          </PostcodesGrid>
        </Container>
      </PostcodesSection>

      <FAQSection>
        <Container>
          <FAQHeader>
            <div className="subtitle">Got Questions?</div>
            <div className="title">Frequently Asked Questions</div>
            <div className="description">
              Everything you need to know about resin bound surfacing in Greenwich. Can't find what you're looking for? 
              <Link to="/contact/" style={{ color: brandTheme.colors.primary[600], textDecoration: 'none', fontWeight: '600' }}> Contact us directly</Link>.
            </div>
          </FAQHeader>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <FAQQuestion
                  onClick={() => toggleFAQ(index)}
                  className={openFAQ === index ? 'active' : ''}
                >
                  <span>{faq.question}</span>
                  <ChevronDown size={20} className="icon" />
                </FAQQuestion>
                {openFAQ === index && (
                  <FAQAnswer
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    {faq.answer}
                  </FAQAnswer>
                )}
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <CTASection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>Ready to Transform Your Greenwich Property?</SectionTitle>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9, color: 'white' }}>
            Join hundreds of satisfied customers across Greenwich who have chosen Resin Surfaces London
          </p>
          <CTAButtons>
            <StyledButton to="/contact/" className="secondary">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton to="https://wa.me/447535935779" className="secondary">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
          </CTAButtons>
          
          <div style={{ marginTop: '3rem', padding: '2rem', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '15px', backdropFilter: 'blur(10px)' }}>
            <h3 style={{ color: 'white', textAlign: 'center', marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              🏆 Greenwich's #1 Choice for Resin Bound Surfacing
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Free Greenwich Survey</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>10+ Year Warranty</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>SUDS Compliant</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>5★ Rated Service</div>
              </div>
            </div>
          </div>
        </Container>
      </CTASection>

      {/* Image Modal */}
      {selectedImageIndex !== null && (
        <ImageModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={closeImageModal}
        >
          <CloseButton onClick={closeImageModal}>
            <X size={24} />
          </CloseButton>
          
          <NavigationButton 
            className="prev" 
            onClick={goToPreviousImage}
            aria-label="Previous image"
          >
            <ChevronLeft size={24} />
          </NavigationButton>
          
          <NavigationButton 
            className="next" 
            onClick={goToNextImage}
            aria-label="Next image"
          >
            <ChevronRight size={24} />
          </NavigationButton>

          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalImage 
              src={galleryItems[selectedImageIndex].image} 
              alt={galleryItems[selectedImageIndex].title}
            />
            <ModalTitle>{galleryItems[selectedImageIndex].title}</ModalTitle>
            <ModalDescription>{galleryItems[selectedImageIndex].description}</ModalDescription>
          </ModalContent>

          <ImageCounter>
            {selectedImageIndex + 1} / {galleryItems.length}
          </ImageCounter>
        </ImageModal>
      )}
    </>
  );
};

export default GreenwichPage;
